import { app } from '@microsoft/teams-js';
import { useTeamsUserCredential } from '@microsoft/teamsfx-react';
import { useEffect, useState } from 'react';
import { createJWTbackendToken } from 'src/utils/jwt/createJWT';
import axios from 'axios';
import { authentication } from '@microsoft/teams-js';

export const useInitializeTeamsApp = () => {
  // Use the Teams User Credential hook to get user credentials and theme
  const { loading, theme, themeString, teamsUserCredential } =
    useTeamsUserCredential({
      initiateLoginEndpoint: process.env.REACT_APP_URL,
      clientId: process.env.REACT_APP_CLIENT_ID,
    });
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [loadingApp, setLoadingApp] = useState(true);
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  const [appHost, setAppHost] = useState('');
  const isLoading = loadingAuth || loadingApp;
  const getAuth = async (authToken: string) => {
    // Send a POST request to the /api/auth endpoint with the authToken
    try {
      const { data } = await axios.post('api/auth', {
        authToken,
      });
      axios.interceptors.request.use((config) => {
        if (data.token) {
          config.headers.Authorization = `Bearer ${data.token}`;
        }
        return config;
      });
    } catch {
      app.notifyFailure({
        reason: app.FailedReason.AuthFailed,
        message: 'Could not authenticate user',
      });
    } finally {
      setLoadingAuth(false);
    }
  };

  useEffect(() => {
    if (!loading) return;

    // Initialize the app
    app
      .initialize()
      .then(() => {
        app.notifySuccess();
      })
      .catch(() => {
        app.notifyFailure({
          reason: app.FailedReason.AuthFailed,
          message: 'Could not authenticate user',
        });
      })
      .finally(() => {
        setLoadingApp(false);
      });

    app.getContext().then(async (context) => {
      const authToken = createJWTbackendToken(
        context.user.tenant.id,
        context.user.userPrincipalName,
        context.user.id
      );
      authentication
        .getAuthToken()
        .then(async (token) => {
          await getAuth(token);
        })
        .catch(async (error) => {
          console.log(error);
          //mainly for local use, if the token is not available, as we cant get token from localhost
          // we can use the authToken which we build using the createJWTbackendToken function
          await getAuth(authToken);
        })
        .finally(() => {
          setCurrentUserEmail(context.user.userPrincipalName);
          setAppHost(context.app.host.clientType);
        });

      return;
    });
  }, [loading]);

  return {
    isLoading,
    theme,
    themeString,
    teamsUserCredential,
    currentUserEmail,
    appHost,
  };
};
